import { useSelectedOrgId } from "@fero/hooks";
import { useQuery } from "@tanstack/react-query";
import type { AppService } from "adl-gen/app-service";
import type { WithDbId } from "adl-gen/common/db";
import type { Product } from "adl-gen/ferovinum/app/db";
import DataLoader from "dataloader";
import { assertNotUndefined } from "utils/hx/util/types";
import { useAppService } from "../../use-app-service";
import { productKeys } from "../query-keys";

interface GetProductQueryParams {
  productId: string;
}

// Singleton dataloader instance
let productsLoader: DataLoader<string, WithDbId<Product> | undefined>;

const fetchProducts = async (
  productIds: readonly string[],
  organisationId: string,
  appService: AppService,
) => {
  const productsResp = await appService.getProducts({
    ids: [...productIds],
    organisationId,
  });

  // Dataloader requires that we return products in the same order as requested - create a map to pick products in O(n) time
  const productMap = new Map<string, WithDbId<Product>>();
  for (const product of productsResp) {
    productMap.set(product.id, product);
  }

  // Map over ids array and pick the products in order - undefined indicates not found
  return productIds.map((id) => productMap.get(id) ?? undefined);
};

/**
 * A hook to get a product by its id, any requests made within a 100ms window will be batched together.
 */
export const useGetProductQuery = (params: GetProductQueryParams) => {
  const { productId } = params;
  const service = useAppService();
  const organisationId = assertNotUndefined(useSelectedOrgId());

  /**
   * NB: For this query we make use of the dataloader in conjunction with react-query.
   * The main reason we want to do this is so that we can batch our requests to the backend without requiring the caller
   * to need to know that it is being batched, this implementation was inspired by this github discussion:
   *
   * https://github.com/TanStack/query/discussions/365#discussioncomment-1867860
   */
  if (!productsLoader) {
    productsLoader = new DataLoader(
      (ids) => fetchProducts(ids, organisationId, service),
      {
        batchScheduleFn: (callback) => setTimeout(callback, 100), // Collects all requests in 100ms window
        cache: false, // <-- IMPORTANT, dataloader doesn't have the same cache management as react-query
      },
    );
  }

  return useQuery({
    queryKey: productKeys.getProducts([productId]),
    queryFn: () => productsLoader.load(productId),
  });
};
